/**
 * 67.68: the dropships and the pod drops heard in play — the host calls `frame` once a frame with the match's state and
 * tick and its ear (where a world point is from the listener). Each flight in the air keeps its continuous voice
 * (dropshipSound.ts, by dropshipMix.ts's rules, its state from `voiceState`: its phase, speed, height and closing speed),
 * and each of its events (dropshipEvents.ts `flightEvents`: touchdown, the ramp, the boots, the warning, the winch …)
 * fires once as the match's tick crosses it (dropshipFx.ts). A pod drop is remembered from when it appears in the state
 * (it leaves the state as it lands), so its whistle, impacts and hatches fire at its `dropTick`. A clock that stands still
 * (paused) fires nothing; muted (or fast-forwarding), every voice is let go.
 *
 * 207 (1): a side's wave ship idles on its pad between runs (core's `parkedShips`), heard near the base (`padState`,
 * `padFade`). Its one voice passes from the pad to the run as it sets out and back to the pad as it lands, so the
 * spool-up and the settle are one sound, never a voice cut and restarted.
 *
 * 69.82 (the owner: "When the dropship dies its sounds play until it respawns"): a ship lost (on its run, or parked on its pad)
 * lets its voice go; it never passes to an empty pad. The gunships' and helicopters' voices go with their flights.
 *
 * 69.59: a helicopter (a flight of the `heli` craft) is heard as one (helicopterSound.ts, in HELI_STYLE: the rotor, the
 * turbines, its wash from the ground under it), its state from `heliState`; of the events, its touchdown and the boots.
 *
 * Reads the state, writes nothing. Cost a frame: per flight in the air one pose and its motion (three poses) and the
 * voice's update; per ship on its pad, its distance and the voice's update; per drop waiting, a filter of its few events.
 * Nothing with no flight, no pad and no drop.
 */
import type { DropshipFlight, GameState, Vec3 } from '../types';
import { PERSON_BODY_HEIGHT, PHYS_DT } from '../constants';
import { parkedShips } from '../core/dropship';
import { flightNow } from '../core/shipNow';
import { groundMaterial } from '../core/groundMaterial';
import { getSurfaceRadius } from '../core/surface';
import { personSetOf } from '../core/scaleProfile';
import { getAudioContext, getSfxBus, initAudio } from './sfx';
import { DROPSHIP_VOICE, hearDropship } from './dropshipMix';
import { DropshipVoice, type DropshipOut } from './dropshipSound';
import { flightEvents, podEvents, eventsCrossed, voiceState, hearDropshipFx, padState, padSpool, padFade, PAD_FX, type DropshipEvent } from './dropshipEvents';
import { playDropshipFx } from './dropshipFx';
import { HELI_STYLE, hearHelicopter, heliState } from './helicopterMix';
import { HelicopterVoice } from './helicopterSound';

type Where = (p: Vec3) => { people: number; pan: number };

/** 69.82: whether a side's pad stands empty — its ship lost, the new one not yet there (core's parkedShips' rule). */
function padEmpty(state: Pick<GameState, 'shipPads'>, team: number, tick: number): boolean {
  const back = state.shipPads?.[team]?.backAt;
  return back !== undefined && tick < back;
}

export class DropshipSounds {
  private readonly flights = new Map<number, { voice: DropshipVoice; events: DropshipEvent[]; people: number | null; team: number; at: { people: number; pan: number } | null; pad?: { goneTick: number; planetIndex: number; dir: Vec3; /** 69.26: its engines' spool when it set out */ spool0: number } }>();
  /** Each side's wave ship on its pad (by team): its voice, and the tick it landed there (null: standing there from the start). */
  private readonly pads = new Map<number, { voice: DropshipVoice; landed: number | null }>();
  /** 69.59: each helicopter in the air: its voice, its events (touchdown, boots), its last distance and speed (the closing speed, the braking). */
  private readonly helis = new Map<number, { voice: HelicopterVoice; events: DropshipEvent[]; people: number | null; speed: number | null }>();
  private readonly drops = new Map<number, { at: Vec3; planetIndex: number; events: DropshipEvent[] }>();
  private lastTick: number | null = null;
  /** × every sound's loudness (1 in play: the SFX bus carries the master volume). */
  volume = 1;
  /** The nodes the events have made (for the sheet's and the probe's count). */
  fxNodes = 0;
  constructor(private readonly out?: DropshipOut) {}

  /** Voices live now (the sheet's count). */
  get live(): number { return this.flights.size + this.pads.size + this.helis.size; }

  /** One frame: the match's state at `tick`, `dt` the frame's seconds; `where` places a world point at the listener. */
  frame(state: Pick<GameState, 'dropships' | 'pods' | 'shipPads' | 'planets' | 'config' | 'turnNumber'>, tick: number, where: Where, muted: boolean, dt: number): void {
    const from = this.lastTick ?? tick;
    this.lastTick = tick;
    const flights = state.dropships?.flights ?? [];
    for (const d of state.pods?.drops ?? []) if (!this.drops.has(d.id)) this.drops.set(d.id, { at: d.at, planetIndex: d.planetIndex, events: podEvents(d.dropTick, d.units.length) });
    if (muted) { this.stop(); this.drops.clear(); return; }
    const io = this.where();
    const person = PERSON_BODY_HEIGHT * personSetOf(state as GameState).k, seconds = tick * PHYS_DT;
    const seen = new Set<number>();
    for (const f of flights) {
      const now = flightNow(state, f, tick);   // 69.17 step 1: the ship as it is now
      if (!now) continue;
      const pose = now.flight!.pose;
      seen.add(f.id);
      if (f.craft === 'heli') { this.heliFrame(state, f, now.flight!, tick, from, where, io, person, seconds, dt); continue; }
      let e = this.flights.get(f.id);
      if (!e) {
        // (207 (1): the wave ship's voice comes with it off its pad)
        const parked = f.fromPad ? this.pads.get(f.team) : undefined;
        if (parked) this.pads.delete(f.team);
        // (69.26: off on its pad, or winding down: its engines wind up from there as it sets out)
        const spool0 = parked ? padSpool(parked.landed === null ? null : (f.calledTick - parked.landed) * PHYS_DT) : 0;
        const times = { payload: f.payload, landTick: f.landTick, exitTick: f.exitTick, liftTick: f.liftTick, goneTick: f.goneTick, riders: f.riders.length, sortie: !!f.sortie, fromPad: !!f.fromPad, calledTick: f.calledTick };
        e = { voice: parked?.voice ?? new DropshipVoice(this.out, n => { this.fxNodes += n; }), events: flightEvents(times), people: null, team: f.team, at: null, ...(f.fromPad ? { pad: { goneTick: f.goneTick, planetIndex: f.planetIndex, dir: f.origin, spool0 } } : {}) };
        this.flights.set(f.id, e);
      }
      const at = where(pose.pos);
      const closing = e.people !== null && dt > 0 ? (e.people - at.people) / dt : 0;
      e.people = at.people;
      e.at = at;
      const kin = now.flight!.kin, pad = f.fromPad ? { since: (tick - f.calledTick) * PHYS_DT, accel: kin?.accel ?? 0, ...(e.pad ? { spool0: e.pad.spool0 } : {}) } : undefined;
      e.voice.volume = this.volume;
      e.voice.update(hearDropship(at.people, at.pan, voiceState(pose.phase, f.payload, tick, f.liftTick, kin?.speed ?? 0, pose.height / person, closing, pad)));
      if (!io) continue;
      const material = groundMaterial(state, f.planetIndex, f.land, seconds);
      for (const ev of eventsCrossed(e.events, from, tick)) this.play(io, ev, at, e.pad && ev.tick === e.pad.goneTick ? groundMaterial(state, f.planetIndex, f.origin, seconds) : material);
    }
    for (const [id, e] of this.helis) if (!seen.has(id)) { e.voice.update(null); this.helis.delete(id); }
    for (const [id, e] of this.flights) {
      if (seen.has(id)) continue;
      // (207 (1): home on its pad — its gear down there if no frame landed on its last tick — its voice stays with it, settling to the idle.
      // 69.82, the owner: "When the dropship dies its sounds play until it respawns": a ship lost on its run (gone before its goneTick,
      // its pad empty till the new one) is not home — its voice is let go, as any other's)
      const home = e.pad && tick >= e.pad.goneTick && !padEmpty(state, e.team, tick) ? e.pad : null;
      if (io && home && e.at) for (const ev of eventsCrossed(e.events, from, home.goneTick)) this.play(io, ev, e.at, groundMaterial(state, home.planetIndex, home.dir, seconds));
      if (home) { this.pads.get(e.team)?.voice.update(null); this.pads.set(e.team, { voice: e.voice, landed: home.goneTick }); } else e.voice.update(null);
      this.flights.delete(id);
    }
    this.padFrame(state, tick, where);
    for (const [id, d] of this.drops) {
      const planet = state.planets[d.planetIndex];
      if (io && planet) {
        const r = getSurfaceRadius(planet, d.at);
        const at = where({ x: planet.position.x + d.at.x * r, y: planet.position.y + d.at.y * r, z: planet.position.z + d.at.z * r });
        const material = groundMaterial(state, d.planetIndex, d.at, seconds);
        for (const ev of eventsCrossed(d.events, from, tick)) this.play(io, ev, at, material);
      }
      if (d.events.every(ev => ev.tick <= tick)) this.drops.delete(id);
    }
  }

  /** 69.59: a helicopter's frame — its voice (its wash heard from the ground under it) and its touchdown and boots as the tick crosses them. */
  private heliFrame(state: Pick<GameState, 'planets' | 'config' | 'turnNumber'>, f: DropshipFlight, fl: NonNullable<ReturnType<typeof flightNow>>['flight'] & object, tick: number, from: number, where: Where, io: DropshipOut | null, person: number, seconds: number, dt: number): void {
    let e = this.helis.get(f.id);
    if (!e) {
      const times = { payload: f.payload, landTick: f.landTick, exitTick: f.exitTick, liftTick: f.liftTick, goneTick: f.goneTick, riders: f.riders.length, sortie: !!f.sortie, fromPad: false, calledTick: f.calledTick };
      e = { voice: new HelicopterVoice(this.out), events: flightEvents(times).filter(ev => ev.kind === 'touch' || ev.kind === 'boots'), people: null, speed: null };
      this.helis.set(f.id, e);
    }
    const pose = fl.pose, at = where(pose.pos), speed = fl.kin?.speed ?? 0;
    const closing = e.people !== null && dt > 0 ? (e.people - at.people) / dt : 0;
    const brake = e.speed !== null && dt > 0 ? (Math.abs(e.speed) - Math.abs(speed)) / dt : 0;
    e.people = at.people; e.speed = speed;
    const planet = state.planets[f.planetIndex];
    let wash = at.people;
    if (planet) {
      const d = { x: pose.pos.x - planet.position.x, y: pose.pos.y - planet.position.y, z: pose.pos.z - planet.position.z }, l = Math.hypot(d.x, d.y, d.z);
      if (l > 0) { const r = getSurfaceRadius(planet, { x: d.x / l, y: d.y / l, z: d.z / l }) / l; wash = where({ x: planet.position.x + d.x * r, y: planet.position.y + d.y * r, z: planet.position.z + d.z * r }).people; }
    }
    e.voice.volume = this.volume;
    e.voice.update(hearHelicopter(HELI_STYLE, at.people, at.pan, heliState(pose.phase, tick, f.liftTick, PHYS_DT, speed, brake, pose.height / person, closing), wash));
    if (!io) return;
    const material = groundMaterial(state, f.planetIndex, f.land, seconds);
    for (const ev of eventsCrossed(e.events, from, tick)) this.play(io, ev, at, material);
  }

  /** The wave ships on their pads: each heard near its base, its engines at the pad's idle (a side whose pad is gone lets its voice go). */
  private padFrame(state: Pick<GameState, 'dropships' | 'shipPads' | 'planets'>, tick: number, where: Where): void {
    if (!state.shipPads && !this.pads.size) return;
    for (const s of parkedShips(state)) {
      const planet = state.planets[s.planetIndex];
      if (!planet) continue;
      let e = this.pads.get(s.team);
      if (!e) { e = { voice: new DropshipVoice(this.out, n => { this.fxNodes += n; }), landed: null }; this.pads.set(s.team, e); }
      const r = getSurfaceRadius(planet, s.dir);
      const at = where({ x: planet.position.x + s.dir.x * r, y: planet.position.y + s.dir.y * r, z: planet.position.z + s.dir.z * r });
      e.voice.volume = this.volume * padFade(at.people);
      e.voice.update(at.people < PAD_FX.range ? hearDropship(at.people, at.pan, padState(e.landed === null ? null : (tick - e.landed) * PHYS_DT)) : null);
    }
    // (a side's ship just home stays in `pads` the tick its run is still listed; one whose pad is gone is let go — 69.82: and one
    // whose parked ship is lost, the pad empty till its new one)
    for (const [team, e] of this.pads) if (!state.shipPads?.[team] || padEmpty(state, team, tick)) { e.voice.update(null); this.pads.delete(team); }
  }

  private play(io: DropshipOut, ev: DropshipEvent, at: { people: number; pan: number }, material: ReturnType<typeof groundMaterial>): void {
    const h = hearDropshipFx(ev.kind, at.people, at.pan, DROPSHIP_VOICE.gain * this.volume);
    if (!h) return;
    this.fxNodes += playDropshipFx(io.ctx, io.dest, ev.kind, h, io.ctx.currentTime + 0.01, { seconds: ev.seconds, index: ev.index, material });
  }

  stop(): void {
    for (const e of this.flights.values()) e.voice.stop();
    for (const e of this.pads.values()) e.voice.stop();
    for (const e of this.helis.values()) e.voice.stop();
    this.flights.clear(); this.pads.clear(); this.helis.clear();
  }

  private where(): DropshipOut | null {
    if (this.out) return this.out;
    initAudio();
    const ctx = getAudioContext(), bus = getSfxBus();
    return ctx && bus && ctx.state !== 'closed' ? { ctx, dest: bus } : null;
  }
}
